import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useEffect } from 'react';
import { MobileCenteredLayout } from '@/components/MobileCenteredLayout/MobileCenteredLayout';
import { OnboardingProgress } from '@/components/OnboardingProgress/OnboardingProgress';
import { TermsCheckboxes } from '@/components/TermsCheckboxes/TermsCheckboxes';
import { TermsCta } from '@/components/TermsCta/TermsCta';
import { TermsDocumentPanel } from '@/components/TermsDocumentPanel/TermsDocumentPanel';
import { TermsFooter } from '@/components/TermsFooter/TermsFooter';
import { TermsHeader } from '@/components/TermsHeader/TermsHeader';
import { TermsModal } from '@/components/TermsModal/TermsModal';
import { useConsentForm } from '@/hooks/useConsentForm';
import { useConsentTerms } from '@/hooks/useConsentTerms';
import { useTermsModal } from '@/hooks/useTermsModal';
import { trackTermsEvent } from '@/helpers/analytics';
import '@/Pages/Terms/TermsPage.sass';
import { WelcomeFooter } from '@/components/WelcomeFooter/WelcomeFooter';

function TermsPageSkeleton(): JSX.Element {
  return (
    <Box data-testid="terms-page-skeleton" aria-hidden>
      <Skeleton variant="text" width="40%" height={28} sx={{ mb: 1 }} />
      <Skeleton variant="text" width="70%" height={36} sx={{ mb: 1 }} />
      <Skeleton variant="text" width="90%" height={20} sx={{ mb: 2 }} />
      <Skeleton variant="rounded" height={320} />
    </Box>
  );
}

export function TermsPage(): JSX.Element {
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  useEffect(() => {
    void trackTermsEvent({ name: 'terms_page_view' });
  }, []);

  const { data, isLoading } = useConsentTerms();
  const {
    form,
    isCtaEnabled,
    isSubmitting,
    setAcceptedTerms,
    setAcceptedPrivacy,
    setAcceptedMarketing,
    submit,
  } = useConsentForm();
  const { openDocument, open, close, accept } = useTermsModal({
    setAcceptedTerms,
    setAcceptedPrivacy,
  });

  return (
    <MobileCenteredLayout>
      <Box
        component="main"
        className={
          prefersReducedMotion
            ? 'terms-page terms-page--reduced-motion'
            : 'terms-page'
        }
        data-testid="terms-page-main"
        data-motion={prefersReducedMotion ? 'reduced' : 'animated'}
        aria-busy={isLoading}
      >
        <OnboardingProgress currentStep={2} />

        {isLoading ? <TermsPageSkeleton /> : null}

        {data && !isLoading ? (
          <>
            <TermsHeader version={data.version} updatedAt={data.updatedAt} />
            <TermsDocumentPanel content={data.terms} />

            <TermsCheckboxes
              form={form}
              onAcceptedTermsChange={setAcceptedTerms}
              onAcceptedPrivacyChange={setAcceptedPrivacy}
              onAcceptedMarketingChange={setAcceptedMarketing}
              onOpenDocument={open}
            />

            <TermsModal
              open={openDocument !== null}
              document={openDocument}
              termsContent={data.terms}
              privacyContent={data.privacy}
              onClose={close}
              onAccept={accept}
            />

            <TermsCta
              disabled={!isCtaEnabled}
              isSubmitting={isSubmitting}
              onSubmit={submit}
            />

            <TermsFooter />
          </>
        ) : null}
      </Box>
      <WelcomeFooter/>
    </MobileCenteredLayout>
  );
}
